<template>
    <div>
        <div v-if="!!card&&!!card.record_id" class="lam-card lam-card-flex uk-width-1-1">
            <div class="uk-panel uk-panel-box">
                <div class="lam-card-header uk-grid uk-grid-small uk-margin-bottom">
                    <div class="uk-width-1-3">
                        <component :is="(entity==='contract_revision')?'h5':'h4'" class="uk-margin-small-top uk-text-nowrap">
                            {{ title }}
                        </component>
                    </div>
                    <div class="uk-width-1-3 uk-text-center">
                        <div v-if="!!entity && (nb_pages > 1)" class="lam-card-revisions-toc">
                            <div class="uk-text-muted">
                                <a
                                    href
                                    :class="{'uk-invisible': state.page>=nb_pages}"
                                    class="uk-button-link uk-margin-small-right"
                                    @click.prevent="goto_page(state.page + 1)"
                                >
                                    <open-icon glyph="chevron-left" />
                                </a>
                                &nbsp;
                                <span v-if="state.page === nb_pages">(Original Version)</span>
                                <span v-else>Rev. {{ nb_pages - state.page }} of {{ nb_pages - 1 }}</span>
                                &nbsp;
                                <a
                                    href
                                    :class="{'uk-invisible': state.page<=1}"
                                    class="uk-button-link uk-margin-small-left"
                                    @click.prevent="goto_page(state.page - 1)"
                                >
                                    <open-icon glyph="chevron-right" />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="uk-width-1-3 uk-text-right" style="padding-top: 4px;">
                        <span v-if="debugging&&!!datasource">{{ datasource.id }} </span>
                        <base-button
                            v-if="editable"
                            size="mini"
                            color="danger"
                            :title="`Delete this ${title}`"
                            @click.prevent="$emit('delete')"
                        >
                            <open-icon glyph="trash-o" />
                        </base-button>

                        <base-button
                            v-if="editable && !!entity && (state.found > 1)"
                            size="mini"
                            color="danger"
                            title="Delete this revision"
                            @click.prevent="act('delete')"
                        >
                            <open-icon glyph="remove" />
                        </base-button>
                        <base-button
                            v-if="is_processable"
                            size="mini"
                            title="Executed"
                            @click.prevent="act('post-process')"
                        >
                            <open-icon glyph="check" />
                        </base-button>
                        <base-button
                            v-if="is_revisable"
                            size="mini"
                            title="Revise"
                            @click.prevent="act('revise')"
                        >
                            <open-icon glyph="retweet" />
                        </base-button>
                        <base-button
                            v-if="is_statusable"
                            size="mini"
                            title="Reject"
                            @click.prevent="act('reject')"
                        >
                            <open-icon glyph="thumbs-o-down" />
                        </base-button>
                        <base-button
                            v-if="is_statusable"
                            size="mini"
                            title="Accept"
                            @click.prevent="act('accept')"
                        >
                            <open-icon glyph="thumbs-o-up" />
                        </base-button>
                        <base-button
                            v-if="is_sendable"
                            size="mini"
                            title="Send"
                            @click.prevent="act('send')"
                        >
                            <open-icon glyph="send-o" />
                        </base-button>
                    </div>
                </div>
                <div class="lam-card-body">
                    <div
                        v-if="loading"
                        class="loader-container"
                    >
                        <loader />
                    </div>
                    <component
                        v-else-if="!!datasource"
                        :is="body_component"
                        :revision-entity="entity"
                        :record="datasource"
                        :parent="card_record"
                        :parent-entity="card.entity"
                        :editable="editable"
                    />
                </div>
                <div v-if="!is_revisionable_rfm_entity(card.entity)" class="lam-card-footer">
                    <hr>
                    <div class="uk-grid uk-grid-small uk-text-small uk-text-muted">
                        <div class="uk-width-1-2">
                            <div>Requested</div>
                            <form-date-input
                                id="requested_at"
                                v-model="card_record.requested_at"
                                name="requested_at"
                                :max-date="0"
                                :editable="editable"
                                :required="false"
                                size="small"
                                :error="has_error('requested_at')"
                                @input="save_field"
                            />
                        </div>
                        <div class="uk-width-1-2">
                            <div :class="{'uk-text-danger': is_overdue}">Due</div>
                            <form-date-input
                                id="due_at"
                                v-model="card_record.due_at"
                                name="due_at"
                                :editable="editable"
                                :required="false"
                                size="small"
                                :error="has_error('due_at')"
                                @input="save_field"
                            />
                        </div>
                        <div class="uk-grid-margin uk-width-1-1">
                            <sent-info-control
                                :record="card_record"
                                :revision-entity="card.entity"
                                :editable="is_business_manager"
                            />
                        </div>
                    </div>
                    <hr>
                    <div class="uk-grid uk-grid-small">
                        <div class="uk-grid-margin uk-width-1-1">
                            <attachment
                                :entity="card.entity"
                                :owner-type="owner_type"
                                :owner-id="card.record_id"
                                :editable="is_sendable"
                                size="small"
                            />
                            <p v-if="is_sendable" class="uk-text-small uk-text-muted uk-text-center">(Attach a document to this <em>{{ card.type }}</em>)</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <modal-dialog
            id="reject-rfm"
            :show.sync="rejecting"
        >
            <template v-slot:title>
                <small>What would you want to do next?</small>
            </template>

            <hr />
            <p>
                We will mark this revision as "rejected". What would you want to do next?
                Start working on a new revision right away?
                Or just mark it as "rejected" for now?
            </p>
            <p class="uk-text-muted">(You can always create a new revision at a later date.)</p>

            <div class="uk-modal-footer uk-text-center">
                <div class="uk-grid">
                    <div class="uk-width-1-3">
                        <base-button
                            size="large"
                            width="1-1"
                            class="uk-width-1-1"
                            @click.prevent="rejecting=false"
                        >
                            Cancel
                        </base-button>
                    </div>
                    <div class="uk-width-1-3">
                        <base-button
                            color="primary"
                            size="large"
                            width="1-1"
                            class="uk-width-1-1"
                            @click.prevent="reject('reject')"
                        >
                            Reject
                        </base-button>
                    </div>
                    <div class="uk-width-1-3">
                        <base-button
                            color="primary"
                            size="large"
                            width="1-1"
                            class="uk-width-1-1"
                            @click.prevent="reject('revise')"
                        >
                            Revise
                        </base-button>
                    </div>
                </div>
            </div>
        </modal-dialog>
    </div>
</template>

<script>
import { mapGetters } from "vuex"

import requests_for_material_utilities from "../../../mixins/RequestsForMaterialUtilities"
import is_remote_data_table_source from "../../../mixins/IsRemoteDataTableSource"
import handles_saving from "../../../mixins/HandlesSaving"
import ui_utilities from "../../../mixins/UiUtilities"

import MaterialRequest from "./MaterialRequest"
import Qualification from "./Qualification"
import RequestForMaterialRevision from "./RequestForMaterialRevision"
import FormDateInput from "../../../ui/FormDateInput"
import Attachment from "../../../ui/Attachment"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import Loader from "../../../ui/Loader"
import SentInfoControl from "./SentInfoControl"
import ModalDialog from "../../../dialogs/ModalDialog/ModalDialog"

export default {
    mixins: [requests_for_material_utilities, is_remote_data_table_source, handles_saving, ui_utilities],
    components: {
        MaterialRequest,
        Qualification,
        RequestForMaterialRevision,
        FormDateInput,
        Attachment,
        BaseButton,
        OpenIcon,
        Loader,
        SentInfoControl,
        ModalDialog
    },
    mounted () {
        this.rebase()
    },
    watch: {
        card: "rebase"
    },
    methods: {
        rebase () {
            this.entity = ""
            if(this.card) this.entity = this.revision_entity_for_rfm_entity(this.card.entity) || this.card.entity
            this.reset()
        },
        refresh () {
            if(!!this.card && !!this.entity && !!this.profile_id) {
                const query = {
                    ...this.state,
                    fields: this.fields,
                    relation_ids: this.relation_ids
                }

                if(this.card.entity === this.entity) {
                    // record already fetched!
                    this.current_records_ids = [this.card.record_id]

                    this.set_state_values({
                        total: 1,
                        found: 1,
                        query_info: {}
                    })

                    this.post_load({})
                } else {
                    query[`${this.card.entity}_id`] = this.card.record_id

                    this.loading = true
                    this.$store.dispatch(
                        "FETCH_RECORDS",
                        {
                            entity: this.entity,
                            query
                        }
                    ).then(response => {
                        this.current_records_ids = response.record_ids

                        this.set_state_values({
                            total: response.total || 0,
                            found: response.found || 0,
                            query_info: response.query_info || {}
                        })

                        this.post_load(response)
                    }).catch(error => {
                        this.$error(error.message)
                    }).then(() => {
                        this.loading = false
                    })
                }
            }
        },
        save_field (value, field) {
            return this.save_field_for_record_id(this.card.entity, this.card.record_id, value, field)
        },
        send_accept_reject_status (status) {
            if(!this.card) return

            const id = (this.card.entity === this.entity) ? this.card.record_id : this.datasource.id

            this.$store.dispatch("RECORD_SAVE", {
                entity: this.entity,
                id,
                data: { response: (status === "revise") ? "rejected" : status }
            }).then(() => {
                if(status === "accept") {
                    if(this.card.entity === "service_request") {
                        if(!this.datasource.phase && !this.datasource.phase_id) this.$emit("service_request_accepted", this.datasource)
                    }
                } else if(status === "revise") this.act("revise")
                this.$emit("refresh", this.card.entity, this.card.record_id)
            }).catch(error => {
                this.$error(error.message)
            })
        },
        reject (next_step) {
            this.rejecting = false
            this.send_accept_reject_status(next_step)
        },
        send () {
            const id = (this.card.entity === this.entity) ? this.card.record_id : this.datasource.id

            this.$store.dispatch("RECORD_SAVE", {
                entity: this.entity,
                id,
                data: { sent_via: true }
            }).then(() => {
                this.$emit("refresh", this.card.entity, this.card.record_id)
            }).catch(error => {
                if(error) this.$error(error.message)
            })
        },
        act (action) {
            if(!this.card) return

            switch (action) {
            case "send":
                this.send()
                break

            case "revise":
                if(this.card.entity === this.entity) return

                this.$store.dispatch("CREATE_RECORD", {
                    entity: this.entity,
                    data: { [`${this.card.entity}_id`]: this.card.record_id, relation_ids: this.relation_ids }
                }).then(record => {
                    this.current_records_ids = [record.id]
                    this.set_state_values({
                        total: this.state.total + 1,
                        found: this.state.found + 1,
                        page: 1
                    })
                    this.$emit("refresh", this.card.entity, this.card.record_id)
                }).catch(error => {
                    if(error) this.$error(error.message)
                })
                break

            case "accept":
                this.send_accept_reject_status("accept")
                break
            case "reject":
                this.rejecting = true
                break

            case "post-process":
                if(this.card.entity === "contract") {
                    this.$store.dispatch("RECORD_SAVE", {
                        entity: this.entity,
                        id: this.datasource.id,
                        data: { executed_at: this.$moment().format("YYYY-MM-DD") }
                    }).then(() => {
                        this.$emit("refresh", this.card.entity, this.card.record_id)
                    }).catch(error => {
                        this.$error(error.message)
                    })
                }
                break

            case "delete":
                this.$confirm({
                    type: "error",
                    title: "Delete revision",
                    message: "Do you really want to delete this revision? Once it's gone, it's gone...",
                    cancel: "Keep It",
                    ok: "Delete It",
                    then: (confirmed) => {
                        if(confirmed) {
                            const id = (this.card.entity === this.entity) ? this.card.record_id : this.datasource.id
                            this.$store.dispatch("RECORD_DELETE", {
                                entity: this.entity,
                                id
                            }).then(() => {
                                let page = this.state.page - 1
                                if(page <= 0) page = 1
                                this.set_state_value("page", page)
                                this.refresh()
                                this.$emit("refresh", this.card.entity, this.card.record_id)
                            }).catch(error => {
                                if(error) this.$error(error.message)
                            })
                        }
                    }
                })
                break
            }
        }
    },
    computed: {
        ...mapGetters(["entity_record"]),
        state_identifier () {
            if(!this.card) return "rfm-details"
            return `rfm-details-${this.card.record_id}`
        },
        title () {
            if(!this.card || this.card.titleless) return ""
            return this.card.title || this.card.type
        },
        body_component () {
            if(this.card) {
                if(this.card.entity === "material_request") return "MaterialRequest"
                if(this.card.entity === "qualification") return "Qualification"
            }
            return "RequestForMaterialRevision"
        },
        card_record () {
            if(!this.card || !this.card.record_id) return {}
            return this.entity_record(this.card.entity, this.card.record_id) || {}
        },
        datasource () {
            if(this.card) {
                if(this.card.entity.match(/^(material_request|qualification)/i)) return this.card_record
                const filtered_rows = this.filtered_rows
                if(!!filtered_rows && !!filtered_rows.length) return filtered_rows[0]
            }
            return null
        },
        is_overdue () {
            if(!this.datasource) return false
            return this.$moment(this.datasource.due_at).isBefore()
        },
        is_sendable () {
            if(!this.editable || !this.datasource) return false
            const sent = !!this.datasource.sent_at
            return !sent
        },
        is_revisable () {
            if(!this.editable || !this.datasource) return false
            if(this.entity === this.card.entity) return false
            if(this.state.page > 1) return false

            const sent = !!this.datasource.sent_at
            return sent && !!this.datasource.rejected_at
        },
        is_processable () {
            if(!this.editable || !this.datasource) return false
            if(this.card.entity !== "contract") return false

            return !!this.datasource.accepted_at
        },
        is_statusable () {
            if(!this.editable || !this.datasource || (this.card.entity === this.entity)) return false
            return !!this.datasource.sent_at && !this.datasource.accepted_at && !this.datasource.rejected_at
        },
        owner_type () {
            if(!this.card) return ""
            if(this.card.entity === "material_request") return "App\\MaterialRequest"
            return "App\\Qualification"
        }
    },
    props: {
        card: {
            validator: prop => !prop || (typeof prop === "object"),
            required: true
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            relation_ids: ["attachment", "firm_proposal_revision", "firm"],
            default_state: {
                per_page: 1,
                page: 1,
                sort_by: "created_at",
                sort_dir: "desc",
                filter_on: null,
                filter: null,
                archived: false,
                search: "",
                total: 0,
                found: 0,
                query_info: {}
            },

            rejecting: false
        }
    }
}
</script>
